import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { ChevronDown } from 'lucide-react'
import { SORT_PRESETS, type Facet, type FacetOption, type ListState, type SortPresetId } from '@region13/shared-types'

/** The list screens' filter and sort controls (spec
 *  docs/superpowers/specs/2026-09-26-shared-list-filters-design.md).
 *  Presentational only: options and selections come from the caller, which
 *  computes them with `facetOptions` / `parseListSearch`. */

const controlClass =
  'min-h-[44px] rounded-lg border border-line bg-surface px-3 text-sm text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon'

const SUMMARY_MAX_CHARS = 28

/** "All divisions", "B10U, G10U", or "5 divisions" once the names stop fitting. */
export function multiSelectSummary(plural: string, options: readonly FacetOption[], selected: readonly string[]): string {
  if (selected.length === 0) return `All ${plural}`
  const labelFor = new Map(options.map((o) => [o.value, o.label]))
  const names = selected.map((v) => labelFor.get(v) ?? v).join(', ')
  return names.length < SUMMARY_MAX_CHARS ? names : `${selected.length} ${plural}`
}

/** Close an open panel on a pointer press outside it, on focus moving outside
 *  it (Tab out), and on Escape, which also returns focus to the trigger.
 *  Lifted from stats-spa's DivisionFilter: a panel that only closes from its
 *  own trigger sits over the list it filters. */
function usePanelDismiss(open: boolean, close: () => void, wrap: RefObject<HTMLElement | null>, trigger: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return
    const outside = (ev: Event) => !!wrap.current && !wrap.current.contains(ev.target as Node)
    const onPointerOrFocus = (ev: Event) => { if (outside(ev)) close() }
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return
      close()
      trigger.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerOrFocus)
    document.addEventListener('focusin', onPointerOrFocus)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerOrFocus)
      document.removeEventListener('focusin', onPointerOrFocus)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close, wrap, trigger])
}

/** True once an open panel, laid out from its trigger's left edge, would run
 *  past the viewport's right edge: the panel then anchors to the trigger's
 *  right edge instead. Measured before paint, so it never flashes. */
function useRunsOffRight(open: boolean, panel: RefObject<HTMLElement | null>): boolean {
  const [offRight, setOffRight] = useState(false)
  useLayoutEffect(() => {
    if (!open) { setOffRight(false); return }
    const rect = panel.current?.getBoundingClientRect()
    const viewport = document.documentElement.clientWidth || window.innerWidth
    if (rect && rect.right > viewport) setOffRight(true)
  }, [open, panel])
  return offRight
}

/** A dropdown of checkboxes. Nothing ticked means every value, and the
 *  trigger says "All <plural>" so that state is never mistaken for "none". */
export function MultiSelectFilter({
  label, plural, options, selected, onChange,
}: {
  label: string
  plural: string
  options: FacetOption[]
  selected: readonly string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const summary = multiSelectSummary(plural, options, selected)
  const close = useCallback(() => setOpen(false), [])
  usePanelDismiss(open, close, wrap, trigger)
  const offRight = useRunsOffRight(open, panel)

  return (
    <div ref={wrap} className="relative">
      <button
        ref={trigger}
        type="button"
        className={`${controlClass} flex min-w-[8rem] items-center gap-2 text-left`}
        // Names the selection, not just the control: the visible text is the
        // only place the current selection shows.
        aria-label={`${label}: ${summary}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-ink-soft" aria-hidden="true" />
      </button>
      {open ? (
        <FilterPanel id={panelId} panelRef={panel} label={label} plural={plural} options={options} selected={selected} onChange={onChange} alignRight={offRight} />
      ) : null}
    </div>
  )
}

/** The open panel: as wide as its longest label, never wider than the
 *  viewport less the page gutters, and a long label wraps inside that. */
function FilterPanel({
  id, panelRef, label, plural, options, selected, onChange, alignRight,
}: {
  id: string
  panelRef: RefObject<HTMLDivElement | null>
  label: string
  plural: string
  options: FacetOption[]
  selected: readonly string[]
  onChange: (next: string[]) => void
  alignRight: boolean
}) {
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  // z-20: above a sticky z-10 table header (#613), below the mobile top bar (z-30).
  return (
    <div
      ref={panelRef}
      id={id}
      role="group"
      aria-label={label}
      className={`absolute ${alignRight ? 'right-0' : 'left-0'} z-20 mt-1 max-h-80 w-max min-w-full max-w-[calc(100vw-2rem)] overflow-y-auto whitespace-normal rounded-md border border-line bg-surface p-2 shadow-lg`}
    >
      {options.length === 0 ? <p className="px-2 py-1.5 text-sm text-ink-soft">Nothing to filter yet.</p> : null}
      {options.map((o) => (
        <label key={o.value} className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded px-2 text-sm text-ink hover:bg-canvas">
          <input type="checkbox" className="h-4 w-4 shrink-0 rounded border-line" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
          {o.label}
        </label>
      ))}
      {selected.length > 0 ? (
        <button type="button" className="mt-1 w-full rounded px-2 py-1.5 text-left text-xs font-medium text-brand-maroon hover:underline" onClick={() => onChange([])}>
          All {plural}
        </button>
      ) : null}
    </div>
  )
}

export interface FilterBarFacet {
  key: string
  label: string
  plural: string
  options: FacetOption[]
  selected: readonly string[]
  onChange: (next: string[]) => void
}

/** The FilterBar's facet props for a screen: each facet's options (keyed by
 *  facet key) and picks, with an `onChange` that hands `set` the whole next
 *  list state. */
export function toBarFacets<Row>(
  facets: readonly Facet<Row>[],
  options: Record<string, FacetOption[]>,
  state: ListState,
  set: (next: ListState) => void,
): FilterBarFacet[] {
  return facets.map((f) => ({
    key: f.key, label: f.label, plural: f.plural,
    options: options[f.key] ?? [],
    selected: state.selection[f.key] ?? [],
    onChange: (next) => set({ ...state, selection: { ...state.selection, [f.key]: next } }),
  }))
}

/** One MultiSelectFilter per facet, the screen's extra controls, and Clear
 *  filters when the caller passes `onClear` (it does so off the default view). */
export function FilterBar({ facets, onClear, children }: { facets: FilterBarFacet[]; onClear?: () => void; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {facets.map((f) => (
        <MultiSelectFilter key={f.key} label={f.label} plural={f.plural} options={f.options} selected={f.selected} onChange={f.onChange} />
      ))}
      {children}
      {onClear ? (
        <button type="button" className={`${controlClass} font-semibold`} onClick={onClear}>
          Clear filters
        </button>
      ) : null}
    </div>
  )
}

/** `defaultLabel` adds a first option standing for no preset (`null`: the
 *  screen's own order), for a list whose `defaultSort` is null. */
type SortSelectProps =
  | { sorts: readonly SortPresetId[]; value: SortPresetId; onChange: (next: SortPresetId) => void; defaultLabel?: undefined }
  | { sorts: readonly SortPresetId[]; value: SortPresetId | null; onChange: (next: SortPresetId | null) => void; defaultLabel: string }

/** A visible "Sort by" beside the select. The select keeps its own
 *  aria-label so its accessible name stays "Sort by" rather than the label's
 *  text plus the chosen option. */
export function SortSelect({ sorts, value, onChange, defaultLabel }: SortSelectProps) {
  return (
    <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
      <span>Sort by</span>
      <select
        aria-label="Sort by"
        className={controlClass}
        value={value ?? ''}
        onChange={(e) => {
          const next = sorts.find((s) => s === e.target.value)
          if (next) onChange(next)
          else if (defaultLabel !== undefined) (onChange as (n: null) => void)(null)
        }}
      >
        {defaultLabel !== undefined ? <option value="">{defaultLabel}</option> : null}
        {sorts.map((s) => (
          <option key={s} value={s}>{SORT_PRESETS[s].label}</option>
        ))}
      </select>
    </label>
  )
}

export function HidePastToggle({ checked, onChange }: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
      <input type="checkbox" className="h-4 w-4 rounded border-line" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      Hide past weeks
    </label>
  )
}
